import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { structureApi, type BranchValueEntry, type BranchValues, type PositionDetail, type PositionNode } from "../../api/structure";
import { countTemplateNodes, templatesApi, type TemplateDetail, type TemplateView } from "../../api/templates";
import type { ApiError } from "../../api/client";
import { Modal } from "../../components/Modal";
import { Select, type SelectOption } from "../../components/Select";
import { PositionKindIcon } from "../../components/PositionKindIcon";
import { AttributeValueChips, filledDefs } from "../../components/AttributeValueChips";
import { CopyExcludePicker } from "./CopyExcludePicker";
import { NO_VALUE, errorBox, positionTitle, textarea, translateError } from "./shared";

// ---------------------------------------------------------------------------
// Save as template (sprint 4)

export function SaveTemplateModal({
  position, onClose, onSaved,
}: {
  position: PositionDetail;
  onClose: () => void;
  onSaved: (saved: TemplateDetail) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(position.name);
  const [description, setDescription] = useState("");
  const [includeRoot, setIncludeRoot] = useState(true);
  // Positions of the branch left out of the template (each with everything below it); empty = the whole branch.
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  // Filled attribute values left out, by position (2026-10-20); empty = every value goes in.
  const [omitted, setOmitted] = useState<Map<string, Set<string>>>(() => new Map());
  const [branch, setBranch] = useState<BranchValues | null>(null);
  const [branchFailed, setBranchFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    structureApi.branchValues(position.id)
      .then((b) => { if (!cancelled) setBranch(b); })
      .catch(() => { if (!cancelled) setBranchFailed(true); });
    return () => { cancelled = true; };
  }, [position.id]);

  // The whole branch, loaded in the tree or not: entries by id, children by parent, filled keys by position.
  const index = useMemo(() => {
    const byId = new Map<string, BranchValueEntry>();
    const childrenOf = new Map<string, string[]>();
    const filled = new Map<string, string[]>();
    for (const e of branch?.positions ?? []) {
      byId.set(e.id, e);
      if (e.parentId) {
        const siblings = childrenOf.get(e.parentId);
        if (siblings) siblings.push(e.id);
        else childrenOf.set(e.parentId, [e.id]);
      }
      const defs = e.expectedTypeId ? branch?.attributeDefs[e.expectedTypeId] ?? [] : [];
      const keys = filledDefs(defs, e.referenceValues).map((d) => d.key);
      if (keys.length > 0) filled.set(e.id, keys);
    }
    return { byId, childrenOf, filled };
  }, [branch]);

  // The positions that become nodes — exact once the branch is here; before that the whole subtree is counted.
  const inTemplate = useMemo(() => {
    if (!branch) return null;
    const out = new Set<string>();
    if (includeRoot) out.add(position.id);
    const walk = (id: string) => {
      for (const c of index.childrenOf.get(id) ?? []) {
        if (excluded.has(c)) continue;
        out.add(c);
        walk(c);
      }
    };
    walk(position.id);
    return out;
  }, [branch, index, excluded, includeRoot, position.id]);
  const nodeCount = inTemplate ? inTemplate.size : includeRoot ? position.descendantCount + 1 : position.descendantCount;

  const values = useMemo(() => {
    let total = 0;
    let kept = 0;
    for (const id of inTemplate ?? []) {
      const keys = index.filled.get(id);
      if (!keys) continue;
      const left = omitted.get(id);
      total += keys.length;
      kept += left ? keys.filter((k) => !left.has(k)).length : keys.length;
    }
    return { total, kept };
  }, [inTemplate, index, omitted]);

  const toggleValue = useCallback((positionId: string, key: string) => {
    setOmitted((cur) => {
      const next = new Map(cur);
      const keys = new Set(next.get(positionId));
      if (keys.has(key)) keys.delete(key);
      else keys.add(key);
      if (keys.size > 0) next.set(positionId, keys);
      else next.delete(positionId);
      return next;
    });
  }, []);

  /** The filled values of one position as ticks, or a word when its type has attributes but none is filled. */
  const renderValues = useCallback((positionId: string, outReason: string | null): ReactNode => {
    const entry = index.byId.get(positionId);
    const defs = entry?.expectedTypeId ? branch?.attributeDefs[entry.expectedTypeId] ?? [] : [];
    if (!entry || defs.length === 0) return null;
    if (!index.filled.has(positionId)) {
      return <span className="attr-chips-none">{t("templates.values.noneFilled", "atributi nisu popunjeni")}</span>;
    }
    const left = omitted.get(positionId);
    return (
      <AttributeValueChips
        defs={defs}
        values={entry.referenceValues}
        isKept={(key) => !left?.has(key)}
        onToggle={(key) => toggleValue(positionId, key)}
        disabledReason={outReason}
      />
    );
  }, [branch, index, omitted, toggleValue, t]);
  const outReason = t("templates.values.outReason", "Pozicija ne ulazi u šablon.");
  const renderExtra = useCallback(
    (n: PositionNode, out: boolean) => renderValues(n.id, out ? outReason : null),
    [renderValues, outReason],
  );

  // The tree box opens for a position without sub-positions too when it has values of its own.
  const rootHasValues = filledDefs(position.attributeDefs, position.referenceValues).length > 0;
  const showScope = position.descendantCount > 0 || rootHasValues;
  const anyValues = index.filled.size > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (nodeCount === 0) {
      setError(t("structure.error.templateEmpty", "Bez korena šablon ne bi imao nijedan čvor — pozicija nema pod-pozicija."));
      return;
    }
    setSubmitting(true);
    try {
      const saved = await templatesApi.fromPosition({
        positionId: position.id,
        name: name.trim(),
        description: description.trim() || null,
        includeRoot,
        excludeIds: excluded.size > 0 ? [...excluded] : undefined,
        omitValues: omitted.size > 0 ? Object.fromEntries([...omitted].map(([id, keys]) => [id, [...keys]])) : undefined,
      });
      await onSaved(saved);
    } catch (err) {
      setError(translateError(t, err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t("structure.modal.saveTemplateTitle", "Sačuvaj kao šablon")}
      subtitle={t("structure.modal.saveTemplateSub", "Kopira granu „{{name}}“ u šablon — bez asseta i šifara. Kasnije se dodaje bilo gde u strukturi.", { name: positionTitle(position) })}
      onClose={onClose}
      width={820}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {showScope && (
          // The whole branch is on the table before saving (Strahinja, 2026-10-09): a click leaves a position out;
          // the filled attribute values sit next to each position, each with its own tick (2026-10-20).
          <div className="copy-scope">
            <div className="copy-scope-head">
              <span className="muted" style={{ fontSize: 12.5 }}>
                {excluded.size === 0
                  ? t("structure.modal.templateScopeAll", "U šablon ulazi cela grana. Klikni na poziciju koju ne želiš u šablonu — izostavlja se sa svime ispod nje.")
                  : t("structure.modal.templateScopeExcluded", "Izostavljeno iz šablona: {{count}} (sa svime ispod).", { count: excluded.size })}
              </span>
              {excluded.size > 0 && (
                <button type="button" className="link-btn" onClick={() => setExcluded(new Set())}>
                  {t("structure.modal.copyReset", "Vrati sve")}
                </button>
              )}
              {anyValues && (
                <span className="copy-scope-values">
                  <span className="muted">{t("templates.values.title", "Vrednosti atributa:")}</span>
                  <button type="button" className="link-btn" onClick={() => setOmitted(new Map())}>
                    {t("templates.values.keepAll", "Označi sve")}
                  </button>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => setOmitted(new Map([...index.filled].map(([id, keys]) => [id, new Set(keys)])))}
                  >
                    {t("templates.values.leaveAll", "Odznači sve")}
                  </button>
                </span>
              )}
            </div>
            <CopyExcludePicker
              rootId={position.id}
              excluded={excluded}
              onChange={setExcluded}
              outTag={t("structure.modal.templateOutTag", "ne ulazi u šablon")}
              ariaLabel={t("structure.modal.templatePickAria", "Pozicije koje ulaze u šablon")}
              ready={branch !== null || branchFailed}
              root={{
                name: position.name,
                kind: position.kind,
                sequenceNo: position.sequenceNo,
                out: !includeRoot,
                tag: includeRoot
                  ? t("structure.modal.templateRootTag", "koren šablona")
                  : t("structure.modal.templateOutTag", "ne ulazi u šablon"),
                extra: renderValues(position.id, includeRoot ? null : t("templates.values.rootOutReason", "Koren ne ulazi u šablon — uključi ga ispod.")),
              }}
              renderExtra={branch ? renderExtra : undefined}
            />
            {branchFailed && (
              <span className="muted" style={{ fontSize: 12.5 }}>
                {t("templates.values.loadFailed", "Vrednosti atributa nisu učitane — šablon čuva sve popunjene vrednosti.")}
              </span>
            )}
          </div>
        )}
        <label className="field">
          <span>{t("templates.field.name", "Naziv")} <span className="req-mark">*</span></span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        </label>
        <label className="field">
          <span>{t("templates.field.description", "Opis")}</span>
          <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} style={textarea} placeholder={t("structure.modal.saveTemplateDescPlaceholder", "npr. Standardni transporter: motor + reduktor + 2 ležaja + lanac")} />
        </label>
        <label className="check-row">
          <input type="checkbox" checked={includeRoot} onChange={(e) => setIncludeRoot(e.target.checked)} />
          {t("structure.modal.includeRoot", "Uključi i ovu poziciju kao koren šablona")}
        </label>
        <div className="muted" style={{ fontSize: 12.5 }}>
          {inTemplate || excluded.size === 0
            ? t("structure.modal.saveTemplateCount", "Šablon će imati {{count}} čvorova.", { count: nodeCount })
            : t("structure.modal.saveTemplateCountPartial", "Šablon će imati manje od {{count}} čvorova — nazivi i ostalo doteruju se u editoru šablona posle čuvanja.", { count: nodeCount })}
          {values.total > 0 && (
            <> {t("templates.values.count", "Vrednosti atributa u šablonu: {{kept}} od {{total}}.", { kept: values.kept, total: values.total })}</>
          )}
        </div>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting || !name.trim()}>
            {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Add from template (sprint 4)

export function ApplyTemplateModal({
  position, onClose, onApplied,
}: {
  position: PositionDetail;
  onClose: () => void;
  onApplied: (created: PositionDetail[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<TemplateView[] | null>(null);
  const [templateId, setTemplateId] = useState<string>(NO_VALUE);
  const [preview, setPreview] = useState<TemplateDetail | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    templatesApi.list({ size: 100 })
      .then((res) => setTemplates(res.content))
      .catch((err: ApiError) => { setTemplates([]); setError(err.message); });
  }, []);

  useEffect(() => {
    if (!templateId) { setPreview(null); return; }
    let cancelled = false;
    setPreview(null);
    templatesApi.get(templateId)
      .then((d) => { if (!cancelled) setPreview(d); })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [templateId]);

  const options = useMemo<SelectOption<string>[]>(
    () => [
      { value: NO_VALUE, label: t("structure.modal.pickTemplate", "— izaberite šablon —") },
      ...(templates ?? []).map((tpl) => ({
        value: tpl.id,
        label: `${tpl.name} (${t("templates.nodeCount", "{{count}} čvorova", { count: tpl.nodeCount })})`,
      })),
    ],
    [templates, t],
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!templateId) {
      setError(t("structure.modal.noTemplate", "Nije izabran šablon."));
      return;
    }
    const qty = Number.parseInt(quantity, 10);
    if (!Number.isInteger(qty) || qty < 1 || qty > 20) {
      setError(t("structure.error.copyQuantity", "Količina mora biti ceo broj od 1 do 20."));
      return;
    }
    setSubmitting(true);
    try {
      await onApplied(await structureApi.applyTemplate(position.id, { templateId, quantity: qty }));
    } catch (err) {
      setError(translateError(t, err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t("structure.modal.applyTemplateTitle", "Dodaj iz šablona")}
      subtitle={t("structure.modal.applyTemplateSub", "Čvorovi šablona postaju nove pod-pozicije ispod „{{name}}“.", { name: positionTitle(position) })}
      onClose={onClose}
      width={640}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label className="field">
          <span>{t("structure.modal.template", "Šablon")} <span className="req-mark">*</span></span>
          {templates === null ? (
            <span className="muted" style={{ fontSize: 12.5 }}>{t("common.loading", "Učitavanje…")}</span>
          ) : templates.length === 0 ? (
            <span className="muted" style={{ fontSize: 12.5 }}>{t("structure.modal.noTemplates", "Još nema šablona — napravite ga akcijom „Sačuvaj kao šablon“ na nekoj grani.")}</span>
          ) : (
            <Select<string> value={templateId} options={options} onChange={setTemplateId} ariaLabel={t("structure.modal.template", "Šablon")} />
          )}
        </label>
        {templateId && (
          <div className="inherited-group">
            <div className="src">{t("structure.modal.templateRoots", "Koreni čvorovi")}</div>
            {preview === null ? (
              <span className="muted" style={{ fontSize: 12.5 }}>{t("common.loading", "Učitavanje…")}</span>
            ) : preview.nodes.length === 0 ? (
              <span className="muted" style={{ fontSize: 12.5 }}>{t("templates.treeEmpty", "Šablon nema čvorova.")}</span>
            ) : (
              <ul>
                {preview.nodes.map((n) => (
                  <li key={n.id}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span className="tree-icon"><PositionKindIcon kind={n.kind} /></span>
                      <b>{n.name}</b>
                      {n.sequenceNo !== null && <span className="muted">#{n.sequenceNo}</span>}
                      {n.expectedTypeName && <span className="muted">· {n.expectedTypeName}</span>}
                      {n.children.length > 0 && (
                        <span className="muted">({t("structure.modal.templateChildren", "+{{count}} ispod", { count: countTemplateNodes(n.children) })})</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="form-grid">
          <label className="field">
            <span>{t("structure.field.quantity", "Količina")} <span className="req-mark">*</span></span>
            <input className="input" type="number" min={1} max={20} value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="numeric" required />
          </label>
          <div className="field">
            <span>&nbsp;</span>
            <span className="muted" style={{ fontSize: 12.5, alignSelf: "center" }}>
              {t("structure.modal.applyQuantityHint", "Više od 1 primenjuje šablon N puta; koreni čvorovi dobijaju redni broj #1…#N.")}
            </span>
          </div>
        </div>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting || !templateId}>
            {submitting ? t("action.saving", "Čuvanje…") : t("structure.addFromTemplate", "Dodaj iz šablona")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
